import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, DataTable, EmptyState, FilterBar, FilterChips, FiltroPill,
  Pagination, Pill, type Column, fmtBRL,
} from "@atlas/ui/web";
import { POR_PAGINA_PADRAO, motivoVazio, recorteDoServidor } from "../../lib/recorte";
import { useDebouncedValue } from "../../lib/use-debounce";
import { useFiltros } from "../../lib/use-filtros";
import { atlas } from "../../lib/sdk";
import { mensagemDeErroCcb } from "../../lib/ccb-erros";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import type { PrefeituraContrato } from "@atlas/sdk";
import { contratoStatusInfo } from "../../lib/contrato-status";
import { produtoLabelDe } from "@atlas/types";
import { PageHeader } from "./_ui";
import { HistoricoPagamentosContratoModal } from "../../components/HistoricoPagamentosContratoModal";

// ISO -> "DD/MM/YYYY" (pt-BR). Null / string invalida -> "—".
function fmtDataAverbacao(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const d = new Date(t);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Contrato considera-se AVERBADO quando entrou no ciclo pos-averbacao —
 *  averbadora aplicou (Ativo/Averbado/Em dia) ou ja foi Quitado. Estados
 *  pre-averbacao (Aguardando, Cancelado, Recusado, Expirado) nao tem CCB. */
function isAverbado(situacao: string): boolean {
  const s = situacao.toLowerCase();
  return s === "ativo" || s === "averbado" || s.includes("em dia") || s.includes("em_dia") || s === "quitado";
}

export function PrefeituraContratos() {
  // A organizacao sobre a qual esta tela opera — a do login, ou a atendida
  // escolhida no seletor do cabecalho. Fora do shell (teste isolado) devolve
  // vazio, e a tela se comporta como antes da feature.
  const { chaveDaUnidade, opcoesDoSdk } = useUnidadeAtiva();
  // Recorte NO SERVIDOR, agora com os filtros do mesmo lado.
  //
  // Ate 04/09/2026 esta tela nao tinha filtro nenhum: exibia ADF, servidor,
  // matricula, banco, tipo, origem e situacao, e nenhum deles era recortavel —
  // em base grande a paginacao era o unico jeito de achar um contrato. Filtro e
  // recorte subiram JUNTOS de proposito: com a pagina no servidor e a busca no
  // cliente, a busca procuraria so' dentro das 25 linhas visiveis e a tela
  // responderia "nao existe" para um ADF que existe.
  //
  // ORIGEM NAO E' FILTRO, por decisao de 04/09/2026. Ela segue como COLUNA — a
  // prefeitura precisa distinguir o que a Atlas originou do que ja estava na
  // folha —, mas nao como recorte: sao dois valores, e a prefeitura desconta os
  // dois do mesmo jeito, entao separar por origem nao muda a acao dela.
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  const [banco, setBanco] = useState("");
  // A busca vai para a rota, entao espera a pausa de digitacao — um request por
  // tecla nao aparece na tela, aparece na conta.
  const buscaDebounced = useDebouncedValue(busca);

  const q = useQuery({
    // A chave carrega a unidade ativa (009) E o recorte com filtros (04/09):
    // sem a primeira, trocar de unidade reaproveitaria o cache da anterior.
    queryKey: ["prefeitura", "contratos", ...chaveDaUnidade(), buscaDebounced, situacao, banco, pagina, porPagina],
    queryFn: () => atlas.prefeitura.contratos({
      q: buscaDebounced || undefined,
      situacao: situacao || undefined,
      banco: banco || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }, opcoesDoSdk()),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (prev) => prev,
  });
  const recorte = recorteDoServidor(q.data?.contratos ?? [], q.data?.total ?? 0, pagina, porPagina);
  // Facetas vem da API porque, depois do recorte, so' o servidor ainda tem o
  // conjunto inteiro para contar. Montar as opcoes com `recorte.itens` daria
  // chips da PAGINA — e um banco desapareceria da lista ao virar de pagina.
  const facetas = q.data?.facetas ?? { bancos: [], situacoes: [] };
  const [downloading, setDownloading] = useState<string | null>(null);
  // O historico de pagamentos abre DAQUI, e nao mais da tela de ADF (15/09/2026).
  // A ADF responde pelo lote do mes; quanto de cada parcela entrou de fato é
  // atributo do contrato, e era ali que a prefeitura ia procurar.
  const [detalhes, setDetalhes] = useState<PrefeituraContrato | null>(null);

  const filtros = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "situacao", ligado: situacao !== "", limpar: () => setSituacao("") },
      { nome: "banco", ligado: banco !== "", limpar: () => setBanco("") },
    ],
    setPagina,
  );
  const { filtrar, temFiltro } = filtros;

  async function baixarPdf(adf: string) {
    setDownloading(adf);
    try {
      const blob = await atlas.prefeitura.baixarContratoCcb(adf);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrato-${adf}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const err = e as Error & { status?: number; reason?: string };
      // Traducao compartilhada com o portal do servidor (lib/ccb-erros.ts).
      // `contrato_nao_e_desta_prefeitura` deixou de existir: contrato de outra
      // organizacao agora responde 404 `contrato_nao_encontrado`, identico ao de
      // ADF inexistente — o 403 anterior confirmava a existencia de contrato de
      // tenant alheio e permitia enumerar a base inteira.
      console.warn("CCB: download falhou", { status: err.status, reason: err.reason });
      alert(mensagemDeErroCcb(err, "prefeitura"));
    } finally {
      setDownloading(null);
    }
  }

  const columns: Column<PrefeituraContrato>[] = [
    { key: "adf", header: "ADF", mono: true },
    { key: "nome", header: "Servidor" },
    { key: "matricula", header: "Matrícula", mono: true },
    { key: "bancoNome", header: "Consignatária" },
    // Tipo cru do backend (ex.: "CARTAO_BENEFICIO") -> label PT-BR via helper
    // unificado do frontend. Backend ja passa a chave canonica via
    // deriveProdutoLabel (portal-banco/store), so falta traduzir aqui.
    // Cliente 28/07/2026.
    { key: "tipoContrato", header: "Tipo", render: (c) => produtoLabelDe(c.tipoContrato) },
    // A prefeitura desconta as duas origens do mesmo jeito — esta coluna existe
    // pra ela distinguir o que a Atlas originou do que ela já tinha na folha
    // quando entrou. Antes as legadas não apareciam nesta tela: o filtro era por
    // convênio, e contrato legado não tem convênio.
    { key: "origem", header: "Origem", render: (c) => (
      c.origem === "legado"
        ? (
          <span title="Já estava na sua folha quando a Atlas entrou, ou foi aceita na conciliação">
            <Pill variant="pendente">legado</Pill>
          </span>
        )
        : <span style={{ color: "var(--text-dim)" }}>Atlas</span>
    ) },
    { key: "valorParcela", header: "Parcela", align: "right", render: (c) => fmtBRL(c.valorParcela) },
    { key: "totalParcelas", header: "Parcelas", align: "right" },
    { key: "averbadoEm", header: "Averbado em", render: (c) => fmtDataAverbacao(c.averbadoEm) },
    { key: "situacao", header: "Situação", render: (c) => {
      // Rotulo unificado com averbadora/banco/servidor (lib/contrato-status).
      const info = contratoStatusInfo(c.situacao);
      return (
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <Pill variant={info.variant}>{info.label}</Pill>
          {/* O selo marca a LINHA onde a folha descontou menos que a parcela,
              para a prefeitura achar o que conferir sem abrir um modal por
              contrato. Ele nao contradiz a situacao: a parcela FOI encerrada e o
              cronograma andou — o selo informa a diferenca, que o banco credor
              cobra por fora, e nao uma pendencia da folha. */}
          {c.pagamentosParciais > 0 ? (
            <span title={`${c.pagamentosParciais} competência(s) descontaram só parte da parcela. Veja em "Ver detalhes".`}>
              <Pill variant="emdia">{c.pagamentosParciais}× parcial</Pill>
            </span>
          ) : null}
        </span>
      );
    } },
    // Em TODAS as linhas, e nao so' nas que tem parcial: o historico responde
    // "como esta a divida deste servidor", e quem confere um contrato integral
    // precisa ver que ele descontou integral. Oferecer a acao so' onde ha selo
    // faria a ausencia de resposta parecer ausencia de dado.
    { key: "acoes", header: "", align: "right", render: (c) => (
      <Button size="sm" variant="ghost" onClick={() => setDetalhes(c)}>Ver detalhes</Button>
    ) },
    // Coluna nova (cliente 31/07/2026) — prefeitura precisa baixar o PDF do
    // contrato assinado (CCB anexado pelo banco). So aparece pra contratos
    // averbados (ativo/em_dia/quitado); nos outros mostra "—".
    { key: "adf", header: "PDF", align: "right", render: (c) => (
      isAverbado(c.situacao) ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => baixarPdf(c.adf)}
          disabled={downloading === c.adf}
        >
          {downloading === c.adf ? "..." : "↓ PDF"}
        </Button>
      ) : (
        <span style={{ color: "var(--text-dim)", fontSize: 12 }}>—</span>
      )
    ) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* O subtitulo passa a dizer o que o numero e'. Antes era "Total: N" com
          N = base inteira; agora N e' o RECORTE, e com filtro aplicado ele
          precisa declarar de que conjunto fala — senao o operador le a
          contagem do filtro como o tamanho da carteira do municipio. */}
      <PageHeader
        title="Contratos do município"
        subtitle={
          temFiltro
            ? `${recorte.total} de ${q.data?.totalBase ?? 0} contratos · recorte filtrado`
            : `Total: ${q.data?.totalBase ?? 0} · inclui ativos, em análise e cancelados`
        }
      />

      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por ADF, nome, CPF ou matrícula"
        {...filtros.propsFilterBar}
        actions={
          <FilterChips
            variant="segmentado"
            label="Situação"
            value={situacao}
            onChange={filtrar(setSituacao)}
            options={[
              { value: "", label: "Todas" },
              ...facetas.situacoes.map((s) => ({
                value: s.valor,
                label: `${contratoStatusInfo(s.valor).label} (${s.contagem})`,
              })),
            ]}
          />
        }
      >
        <FiltroPill
          label="Consignatária"
          opcoes={facetas.bancos.map((b) => ({ value: b.valor, label: `${b.valor} (${b.contagem})` }))}
          // A rota aceita UM banco. Comparar dois bancos lado a lado e'
          // necessidade da visao global da averbadora, nao da prefeitura, que
          // confere "os contratos do banco X". A pill segue sendo o controle
          // certo pela cardinalidade (um municipio grande tem dezenas de
          // conveniados) e pela busca que ela ganha acima de 8 opcoes.
          selecionados={banco ? [banco] : []}
          onChange={(vs: string[]) => filtrar(setBanco)(vs[vs.length - 1] ?? "")}
          buscaPlaceholder="Buscar consignatária"
        />

      </FilterBar>

      <DataTable
        columns={columns}
        rows={recorte.itens}
        rowKey={(c) => c.adf}
        loading={q.isLoading}
        // `totalBase` vem da rota justamente para esta distincao: com so'
        // `total`, "nenhum contrato averbado" e "nada bate com o filtro" dao
        // ambos 0 e a tela escolheria a mensagem errada em metade dos casos.
        emptyState={
          q.isError
            ? "Não foi possível carregar os contratos. Tente novamente."
            : <EmptyState
                motivo={motivoVazio(q.data?.totalBase ?? 0, recorte.total)}
                filtroVazio="Nenhum contrato bate com esses filtros. Limpe os filtros para ver a carteira inteira."
                semDados="Nenhum contrato averbado nesta prefeitura ainda."
                onLimpar={filtros.limparFiltros}
              />
        }
      />
      <Pagination
        total={recorte.total}
        pagina={recorte.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="contratos"
      />
      {detalhes ? (
        <HistoricoPagamentosContratoModal
          adf={detalhes.adf}
          carregar={() => atlas.prefeitura.historicoPagamentosAdf(detalhes.adf, opcoesDoSdk())}
          operacional
          onClose={() => setDetalhes(null)}
        />
      ) : null}
    </div>
  );
}
